/**
 * When a newer build is live, the app moves onto it without interrupting work:
 *
 * - It switches quietly on the next page change, when nothing is unsaved and
 *   the page being left is not an editor (see `shouldSwitchOnNavigate`).
 * - The notice only appears for someone who has stayed on one page a while, and
 *   never on an editor or over unsaved work. "Later" hides it until the next
 *   release. A user on Quiet (Settings → Account) gets a dashboard pill instead.
 * - Backstop for both: a critical release (`version.json` `critical: true`), or
 *   a tab more than a day behind, still shows the notice, under the same
 *   never-while-editing rule.
 */
export type UpdateNoticeMode = 'standard' | 'quiet';
export const DEFAULT_UPDATE_NOTICE_MODE: UpdateNoticeMode = 'standard';
export const UPDATE_NOTICE_MODE_ATTRIBUTE = 'data-update-notices';
export function isUpdateNoticeMode(value: unknown): value is UpdateNoticeMode {
  return value === 'standard' || value === 'quiet';
}
export function readDocumentUpdateNoticeMode(): UpdateNoticeMode {
  if (typeof document === 'undefined') return DEFAULT_UPDATE_NOTICE_MODE;
  const value = document.documentElement.getAttribute(UPDATE_NOTICE_MODE_ATTRIBUTE);
  return isUpdateNoticeMode(value) ? value : DEFAULT_UPDATE_NOTICE_MODE;
}

/** How long a standard user can stay on one page before the notice appears. */
export const NOTICE_AFTER_MS = 30 * 60_000;
/** How far behind a tab may fall before even Quiet shows the notice. */
export const STALE_AFTER_MS = 24 * 60 * 60_000;

/** Pages where someone builds something; the notice never covers them. */
const EDITOR_PATHS: RegExp[] = [
  /\/(new|edit)(\/|$)/,
  /^\/measurements\/[^/]+\/satellite$/,
  /^\/measurements\/tree-estimator$/,
  /^\/mockups\/(?!$)[^/]+/,
  /^\/mockups-lite(\/|$)/,
  /^\/proposals\/[^/]+$/,
];
export function isEditorPath(pathname: string): boolean {
  return EDITOR_PATHS.some((re) => re.test(pathname));
}

// Editors with unsaved work register here (useUnsavedNavigationGuard does it
// for every editor that warns before leaving).
const unsavedWork = new Set<string>();
export function setUnsavedWork(key: string, unsaved: boolean): void {
  if (unsaved) unsavedWork.add(key);
  else unsavedWork.delete(key);
}
export function hasUnsavedWork(): boolean {
  return unsavedWork.size > 0;
}

export interface UpdateState {
  /** Build id of the newer release, or null when this tab is current. */
  build: string | null;
  /** When this tab first saw that release (ms epoch). */
  since: number | null;
  critical: boolean;
}

export interface NoticeInputs {
  state: UpdateState;
  now: number;
  mode: UpdateNoticeMode;
  pathname: string;
  unsaved: boolean;
  /** The build the user pressed "Later" on. */
  dismissedBuild: string | null;
  /** When the current page was opened (ms epoch). */
  pageSince: number;
}

export function shouldShowNotice(i: NoticeInputs): boolean {
  if (!i.state.build || i.state.since === null) return false;
  if (i.unsaved || isEditorPath(i.pathname)) return false;
  if (i.dismissedBuild === i.state.build) return false;
  const behind = i.now - i.state.since;
  if (i.state.critical || behind >= STALE_AFTER_MS) return true;
  if (i.mode === 'quiet') return false;
  // Standard: only someone parked on one page long enough to miss the quiet switch.
  return i.now - Math.max(i.state.since, i.pageSince) >= NOTICE_AFTER_MS;
}

/**
 * Reload onto the new build as the user changes page, unless that loses work.
 * `alreadyTried` is the release a previous switch in this tab aimed for: if
 * the reload did not land on it (a CDN still serving the old app), switching
 * again would reload on every page change, so the card and pill take over.
 * `toPath` is the page being opened: never reload INTO an editor or a
 * sign-in screen either. Those read one-shot hand-offs from sessionStorage and
 * clear them as they mount (photos picked on a client's Photos tab; the link a
 * held signup was headed for, read by /auth/complete-profile), so a reload as
 * they mount would throw the hand-off away.
 */
export function shouldSwitchOnNavigate(
  state: UpdateState,
  fromPath: string,
  unsaved: boolean,
  alreadyTried: string | null = null,
  toPath: string | null = null,
): boolean {
  return !!state.build && state.build !== alreadyTried && !unsaved && !isEditorPath(fromPath) &&
    !(toPath !== null && (isEditorPath(toPath) || /^\/auth(\/|$)/.test(toPath)));
}

const SWITCHED_KEY = 'lm.app-update.switched-to';
export function readSwitchAttempt(): string | null {
  try { return window.sessionStorage.getItem(SWITCHED_KEY); } catch { return null; }
}
export function recordSwitchAttempt(build: string): void {
  try { window.sessionStorage.setItem(SWITCHED_KEY, build); } catch { /* the in-memory state still stops a loop until reload */ }
}

let updateState: UpdateState = { build: null, since: null, critical: false };
const listeners = new Set<() => void>();
export function getUpdateState(): UpdateState {
  return updateState;
}
export function subscribeUpdateState(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}
export function setUpdateState(next: { build: string | null; critical?: boolean }, now = Date.now()): void {
  const build = next.build;
  const critical = !!build && !!next.critical;
  if (build === updateState.build && critical === updateState.critical) return;
  updateState = {
    build,
    // A second release while waiting keeps the original clock: the tab has
    // been behind since the first one.
    since: build ? updateState.since ?? now : null,
    critical,
  };
  listeners.forEach((l) => l());
}

export interface BuildInfo {
  buildId: string;
  critical: boolean;
}

/** Poll the current origin (including rollbacks), never a deployment-specific URL. */
export function watchAppBuild(
  currentBuild: string,
  onChange: (available: boolean, info?: BuildInfo) => void,
) {
  let stopped = false;
  let pending: AbortController | null = null;
  let lastCheck = -Infinity;

  const check = async () => {
    if (stopped || pending || document.visibilityState === 'hidden' || !navigator.onLine) return;
    if (Date.now() - lastCheck < 15_000) return;
    lastCheck = Date.now();
    const controller = new AbortController();
    pending = controller;
    const timeout = window.setTimeout(() => controller.abort(), 10_000);
    try {
      const response = await fetch(`/version.json?check=${Date.now()}`, {
        cache: 'no-store', signal: controller.signal, credentials: 'omit',
      });
      if (!response.ok || !response.headers.get('content-type')?.includes('application/json')) return;
      const version: unknown = await response.json();
      if (!stopped && typeof version === 'object' && version !== null &&
          'buildId' in version && typeof version.buildId === 'string' && version.buildId.length > 0) {
        const critical = 'critical' in version && version.critical === true;
        onChange(version.buildId !== currentBuild, { buildId: version.buildId, critical });
      }
    } catch {
      // Offline, aborted, or a deployment returning HTML: preserve the page and retry later.
    } finally {
      window.clearTimeout(timeout);
      pending = null;
    }
  };
  const wake = () => { void check(); };
  const timer = window.setInterval(wake, 60_000);
  window.addEventListener('focus', wake);
  window.addEventListener('online', wake);
  document.addEventListener('visibilitychange', wake);
  wake();
  return () => {
    stopped = true;
    pending?.abort();
    window.clearInterval(timer);
    window.removeEventListener('focus', wake);
    window.removeEventListener('online', wake);
    document.removeEventListener('visibilitychange', wake);
  };
}
